import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import type { RadarConfig, RadarTimestep, RadarState } from '../types/radar.types';
import { DEFAULT_RADAR_CONFIG } from '../types/radar.types';
import { useRadarTimesteps } from './useRadarTimesteps';
import { TilePrecacher } from '../services/tilePrecacher';
import MeteomaticsTokenService from '@/services/meteomaticsToken.js';

interface UseRadarLayerOptions {
  enabled: boolean;
  isFuture: boolean;
  config?: Partial<RadarConfig>;
}

interface UseRadarLayerReturn {
  layers: TileLayer[];
  radarState: RadarState;
  currentTimestamp: number | null;
  retry: () => void;
}

function buildTileUrl(
  z: number,
  x: number,
  y: number,
  isoTime: string,
  config: RadarConfig,
  token: string,
): string {
  return (
    `https://api.meteomatics.swiss/tiles/${config.source}/${config.layer}` +
    `/EPSG:3857/${z}/${x}/${y}/${isoTime}/${config.tileSize}.webp` +
    `?style=${config.style}&access_token=${token}`
  );
}

function buildTileUrlTemplate(isoTime: string, config: RadarConfig, token: string): string {
  return (
    `https://api.meteomatics.swiss/tiles/${config.source}/${config.layer}` +
    `/EPSG:3857/{z}/{x}/{y}/${isoTime}/${config.tileSize}.webp` +
    `?style=${config.style}&access_token=${token}`
  );
}

async function fetchTileBitmap(url: string, signal?: AbortSignal): Promise<ImageBitmap> {
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const b = await r.blob();
  return createImageBitmap(b);
}

const renderBitmapSubLayers = (props: any) => {
  const { data } = props;
  if (!data) return null;
  const {
    bbox: { west, south, east, north },
  } = props.tile;
  return new BitmapLayer(props, {
    data: undefined,
    image: data,
    bounds: [west, south, east, north],
    textureParameters: {
      minFilter: 'nearest',
      magFilter: 'nearest',
    },
  });
};

const makeGetTileData =
  (iso: string, cfg: RadarConfig, tkn: string, precacher: TilePrecacher) =>
  async (tile: any) => {
    const { x, y, z } = tile.index;
    const { signal } = tile;
    const url = buildTileUrl(z, x, y, iso, cfg, tkn);
    const cached = precacher.get(url);
    if (cached) return cached;
    return fetchTileBitmap(url, signal);
  };

const INITIAL_STATE: RadarState = {
  status: 'idle',
  activeIndex: 0,
  totalCount: 0,
  precacheProgress: 0,
  errorMessage: null,
};

export function useRadarLayer({
  enabled,
  isFuture,
  config = {},
}: UseRadarLayerOptions): UseRadarLayerReturn {
  const mergedConfig = useMemo(() => ({ ...DEFAULT_RADAR_CONFIG, ...config }), [config]);
  const timesteps = useRadarTimesteps(isFuture, mergedConfig.timestepCount);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [token, setToken] = useState<string | null>(null);
  const [radarState, setRadarState] = useState<RadarState>({ ...INITIAL_STATE });

  const precacherRef = useRef(new TilePrecacher(mergedConfig.maxConcurrentPrefetch));
  const abortRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryKeyRef = useRef(0);
  const viewportCapturedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const timestepsRef = useRef<RadarTimestep[]>(timesteps);
  const configRef = useRef(mergedConfig);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    timestepsRef.current = timesteps;
  }, [timesteps]);
  useEffect(() => {
    configRef.current = mergedConfig;
  }, [mergedConfig]);

  const fetchToken = useCallback(async () => {
    try {
      const t = await MeteomaticsTokenService.getValidToken();
      setToken(t);
      setRadarState({
        status: 'loading',
        activeIndex: 0,
        totalCount: timesteps.length,
        precacheProgress: 0,
        errorMessage: null,
      });
    } catch {
      setRadarState({
        status: 'error',
        activeIndex: 0,
        totalCount: 0,
        precacheProgress: 0,
        errorMessage: 'Failed to authenticate with Meteomatics',
      });
    }
  }, [timesteps.length]);

  const startPrecaching = useCallback(
    async (tileCoords: Array<{ x: number; y: number; z: number }>) => {
      const currentToken = tokenRef.current;
      const steps = timestepsRef.current;
      const cfg = configRef.current;

      if (!currentToken || steps.length <= 1) {
        setRadarState((s) => ({ ...s, status: 'loaded', precacheProgress: steps.length }));
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setRadarState((s) => ({ ...s, status: 'precaching', precacheProgress: 1 }));

      for (let i = 1; i < steps.length; i++) {
        if (controller.signal.aborted) return;

        const urls = tileCoords.map(({ x, y, z }) =>
          buildTileUrl(z, x, y, steps[i].isoString, cfg, currentToken),
        );

        try {
          await precacherRef.current.prefetchUrls(urls, controller.signal);
        } catch {
          if (controller.signal.aborted) return;
        }

        if (!controller.signal.aborted) {
          setRadarState((s) => ({ ...s, precacheProgress: i + 1 }));
        }
      }

      if (!controller.signal.aborted) {
        setRadarState((s) => ({ ...s, status: 'loaded' }));
      }
    },
    [],
  );

  const handleViewportLoad = useCallback(
    (tiles: any[]) => {
      if (viewportCapturedRef.current || !tiles || tiles.length === 0) return;
      viewportCapturedRef.current = true;

      const coords = tiles
        .filter((t: any) => t.isVisible)
        .map((t: any) => ({
          x: t.index.x,
          y: t.index.y,
          z: t.index.z,
        }));

      if (coords.length > 0) {
        startPrecaching(coords);
      }
    },
    [startPrecaching],
  );

  useEffect(() => {
    if (!enabled) {
      setToken(null);
      setCurrentIndex(0);
      viewportCapturedRef.current = false;
      abortRef.current?.abort();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      precacherRef.current.clear();
      setRadarState({ ...INITIAL_STATE });
      return;
    }

    abortRef.current?.abort();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    precacherRef.current.clear();
    setCurrentIndex(0);
    viewportCapturedRef.current = false;
    retryKeyRef.current++;
    setToken(null);
    setRadarState({ ...INITIAL_STATE });
    fetchToken();
  }, [enabled, isFuture]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (radarState.status !== 'loaded' || !enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (timesteps.length <= 1) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % timesteps.length;
        setRadarState((s) => ({ ...s, activeIndex: next }));
        return next;
      });
    }, mergedConfig.animationIntervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, radarState.status, timesteps.length, mergedConfig.animationIntervalMs]);

  const layers = useMemo((): TileLayer[] => {
    if (!enabled || !token || timesteps.length === 0) return [];

    const precacher = precacherRef.current;
    const cfg = mergedConfig;
    const tkn = token;
    const isFirstLoad = radarState.status === 'loading';

    if (isFirstLoad) {
      const ts = timesteps[0];
      return [
        new TileLayer({
          id: `radar-tile-${ts.timestamp}-${retryKeyRef.current}`,
          data: buildTileUrlTemplate(ts.isoString, cfg, tkn),
          tileSize: cfg.tileSize,
          minZoom: cfg.minZoom,
          maxZoom: cfg.maxZoom,
          opacity: cfg.opacity,
          maxRequests: cfg.maxRequests,
          debounceTime: cfg.debounceTime,
          refinementStrategy: 'no-overlap',
          onViewportLoad: handleViewportLoad,
          getTileData: makeGetTileData(ts.isoString, cfg, tkn, precacher),
          onTileError: () => {
            setRadarState((prev) => {
              if (prev.status === 'loading') {
                return { ...prev, status: 'error', errorMessage: 'Failed to load radar tiles' };
              }
              return prev;
            });
          },
          renderSubLayers: renderBitmapSubLayers,
        }),
      ];
    }

    return timesteps.map((ts: RadarTimestep, idx: number) => {
      const isActive = idx === currentIndex;

      return new TileLayer({
        id: `radar-tile-${ts.timestamp}-${retryKeyRef.current}`,
        data: buildTileUrlTemplate(ts.isoString, cfg, tkn),
        visible: isActive,
        tileSize: cfg.tileSize,
        minZoom: cfg.minZoom,
        maxZoom: cfg.maxZoom,
        opacity: cfg.opacity,
        maxRequests: cfg.maxRequests,
        debounceTime: cfg.debounceTime,
        refinementStrategy: 'no-overlap',
        getTileData: makeGetTileData(ts.isoString, cfg, tkn, precacher),
        renderSubLayers: renderBitmapSubLayers,
      });
    });
  }, [enabled, token, timesteps, currentIndex, radarState.status, mergedConfig, handleViewportLoad]);

  const currentTimestamp = useMemo((): number | null => {
    if (!enabled || timesteps.length === 0) return null;
    const safeIndex = currentIndex % timesteps.length;
    return timesteps[safeIndex]?.timestamp ?? null;
  }, [enabled, timesteps, currentIndex]);

  const retry = useCallback(() => {
    retryKeyRef.current++;
    viewportCapturedRef.current = false;
    precacherRef.current.clear();
    abortRef.current?.abort();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCurrentIndex(0);
    setToken(null);
    setRadarState({ ...INITIAL_STATE });
    fetchToken();
  }, [fetchToken]);

  return { layers, radarState, currentTimestamp, retry };
}
